import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridOverlay,
  GridPagination,
} from "@mui/x-data-grid";
import { CircularProgress, Button, Stack } from "@mui/material";
import { Add as IconAdd } from "@mui/icons-material";

/**
 * Componente DataGrid reutilizable con:
 * - Toolbar superior: botón "Nuevo", exportar CSV y paginación arriba.
 * - Overlay de carga (spinner)
 * - Estilos unificados para headers, filas, y espaciado.
 * - Muestra los primeros 50 registros ordenados por FechaRegistro descendente
 *   cuando el listado trae ese campo.
 */
const DataGridBase = ({
  rows = [],
  columns = [],
  loading = false,
  toolbarButtons = [],
  pageSize = 50,
  pageSizeOptions = [10, 20, 50, 100],
  // onNewClick,
  // hideNewButton = false,
  sx = {},
  // buttonText = "Nuevo",
  showExport = true,
  paginationTop = true,
  disabled = false,
  ...props
}) => {
  // 🔹 Toolbar combinada
  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{
        justifyContent: "space-between",
        paddingX: 0,
        paddingY: 0,
      }}
    >
      <Stack direction="row" spacing={1}>
        {/* {!hideNewButton && (
          <Button
            size="small"
            startIcon={<IconAdd />}
            onClick={onNewClick}
          >
            {buttonText}
          </Button>
        )} */}
        {toolbarButtons.map((button, index) => (
          <Button
            key={index}
            size="small"
            startIcon={button.icon}
            onClick={button.onClick}
            disabled={disabled || button.disabled}
          >
            {button.text}
          </Button>
        ))}
        {showExport && <GridToolbarExport />}
      </Stack>

      {paginationTop && <GridPagination />}
    </GridToolbarContainer>
  );

  // 🔹 Capa de carga
  const CustomLoadingOverlay = () => (
    <GridOverlay>
      <CircularProgress size={30} />
    </GridOverlay>
  );

  const showToolbar =
    toolbarButtons.length > 0 || showExport;

  // Todos los listados se muestran ordenados por la fecha de registro, de mas
  // reciente a mas antiguo. Para poder ordenar se agrega la columna si el
  // listado no la declara, y queda oculta.
  const declaraFechaRegistro = columns.some(
    (column) => column.field === "fechaRegistro"
  );

  const columnas = declaraFechaRegistro
    ? columns
    : [
        ...columns,
        { field: "fechaRegistro", headerName: "Fecha Registro", type: "date" },
      ];

  const estadoInicial = {
    pagination: {
      paginationModel: {
        pageSize,
      },
    },
    sorting: {
      sortModel: [{ field: "fechaRegistro", sort: "desc" }],
    },
    ...(!declaraFechaRegistro && {
      columns: {
        columnVisibilityModel: { fechaRegistro: false },
      },
    }),
  };

  return (
    <DataGrid
      rows={rows}
      columns={columnas}
      loading={loading}
      autoHeight
      disableColumnMenu
      rowHeight={25}
      columnHeaderHeight={32}
      pageSizeOptions={pageSizeOptions}
      initialState={estadoInicial}
      slots={{
        ...(showToolbar && { toolbar: CustomToolbar }),
        loadingOverlay: CustomLoadingOverlay,
      }}
      localeText={{
        toolbarExport: "",
        toolbarExportCSV: "Descargar CSV",
        toolbarExportPrint: "Imprimir",
        MuiTablePagination: {
            labelRowsPerPage: '', // 👈 deja vacío el texto "Rows per page"
        },
      }}
      sx={{
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: "#1976d2",
          color: "white",
        },
        "& .MuiDataGrid-cell:hover": {
          color: "primary.main",
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
        "& .MuiDataGrid-footerContainer": {
          display: paginationTop ? "none" : "flex",
        },
        "& .MuiDataGrid-toolbarContainer": {
          minHeight: 28,
          padding: 0,
          gap: 0.5,
        },
        "& .MuiTablePagination-root": {
          minHeight: 28,
          margin: 0,
          padding: 0,
          fontSize: "0.75rem",
        },
        "& .MuiTablePagination-toolbar": {
          minHeight: 28,
          padding: 0,
        },
        "& .MuiTablePagination-displayedRows, & .MuiTablePagination-actions": {
          margin: 0,
          padding: 0,
        },
        overflowX: "auto",
        overflowY: "hidden",
        "& .MuiDataGrid-main": {
          overflowX: "auto",
          overflowY: "hidden",
        },
        ...sx,
      }}
      {...props}
    />
  );
};

export default DataGridBase;
